import { action, computed, observable, reaction } from 'mobx'
import { task, type Task } from 'mobx-task'
import { FilingStatus } from '@taxfyle/txf-api-client'
import { match } from '@/utils/pattern-match-util'
import { Id } from '@/infra/id'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import type {
  SnackbarErrorHandlerFactory,
  SnackbarFactory,
} from '@/features/shell'
import type { TxfWorklayerJobStore } from '@/features/jobs/details/tax-type/tax-prep-organizer/TxfWorklayerJobStore'
import {
  type BusinessActivity,
  type Dependent,
  type RentalActivity,
  type TaxPreparationData,
  type TaxActivityAssignee,
  type FilingYear,
  type TxfWorklayerJob,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/models'
import { TaxpayerAccordionViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/personal/components/TaxpayerAccordion/TaxpayerAccordionViewModel'
import { SpouseAccordionViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/personal/components/SpouseAccordion/SpouseAccordionViewModel'
import { DependentAccordionViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/personal/components/DependentAccordion/DependentAccordionViewModel'
import { BusinessAccordionViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/business/components/BusinessAccordion/BusinessAccordionViewModel'
import { RentalAccordionViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/rentals/components/RentalAccordion/RentalAccordionViewModel'
import {
  AddDependentDialogViewModel,
  type NewDependentData,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/personal/components/AddDependentDialog/AddDependentDialogViewModel'
import {
  AddBusinessDialogViewModel,
  type NewBusinessData,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/business/components/AddBusinessDialog/AddBusinessDialogViewModel'
import {
  AddRentalDialogViewModel,
  type NewRentalData,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/rentals/components/AddRentalDialog/AddRentalDialogViewModel'
import { RemoveDependentDialogViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/personal/components/RemoveDependentDialog/RemoveDependentDialogViewModel'
import { RemoveBusinessDialogViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/business/components/RemoveBusinessDialog/RemoveBusinessDialogViewModel'
import { RemoveRentalDialogViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/rentals/components/RemoveRentalDialog/RemoveRentalDialogViewModel'
import { SaveSectionDialogViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/SaveSectionDialog/SaveSectionDialogViewModel'
import { randomUUID } from '@/utils/uuid-util'
import { formatFullName } from '@/utils/full-name-util'

const SNACKBAR_KEY = Symbol('tax-prep-organizer-save')

export enum OrganizerStep {
  Personal = 'Personal',
  Business = 'Business',
  Rental = 'Rental',
}

export type NavigationTarget =
  | { type: 'step'; step: OrganizerStep }
  | { type: 'year'; year: FilingYear }

export type Owner =
  | {
      type: 'UNASSIGNED'
      label: 'Unassigned'
    }
  | {
      type: 'TAX_PAYER'
      label: string
      taxPayerId: string
    }
  | {
      type: 'SPOUSE'
      label: string
      spouseId: string
    }
  | {
      type: 'JOINT'
      label: 'Joint'
      taxPayerId: string
      spouseId: string
    }

export function mapOwnerToTaxActivityAssignee(
  owner: Owner,
): TaxActivityAssignee {
  return match(owner, 'type', {
    TAX_PAYER: ({ taxPayerId }) => ({ type: 'TAX_PAYER', id: taxPayerId }),
    SPOUSE: ({ spouseId }) => ({ type: 'SPOUSE', id: spouseId }),
    JOINT: ({ taxPayerId, spouseId }) => ({
      type: 'JOINT',
      taxPayerId,
      spouseId,
    }),
    UNASSIGNED: () => ({ type: 'UNASSIGNED' }),
  }) as TaxActivityAssignee
}

export class TaxPrepOrganizerViewModel implements ActivationLifecycle {
  readonly lifecycle = new LifecycleTracker()

  private _disposeTaxpayerSpouseSync: (() => void) | null = null
  private _disposeShowSpouseSync: (() => void) | null = null
  @observable accessor selectedStep: OrganizerStep = OrganizerStep.Personal
  @observable accessor showValidationError = false

  @observable accessor taxpayerVm: TaxpayerAccordionViewModel =
    new TaxpayerAccordionViewModel()
  @observable accessor spouseVm: SpouseAccordionViewModel =
    new SpouseAccordionViewModel()

  private readonly _dependentVmList = new ViewModelList<
    Dependent,
    DependentAccordionViewModel
  >({
    models: () => this.taxPrepData.basicInformation.activeDependents,
    create: (model) => {
      const vm = new DependentAccordionViewModel(model)
      this.lifecycle.manage(vm)
      return vm
    },
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  private readonly _businessVmList = new ViewModelList<
    BusinessActivity,
    BusinessAccordionViewModel
  >({
    models: () => this.taxPrepData.activeBusinesses,
    create: (model) => {
      const index = this.taxPrepData.activeBusinesses.indexOf(model)
      const vm = new BusinessAccordionViewModel(
        model,
        index,
        this.selectedYear.year,
        this._getOwners,
      )
      this.lifecycle.manage(vm)
      return vm
    },
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  private readonly _rentalVmList = new ViewModelList<
    RentalActivity,
    RentalAccordionViewModel
  >({
    models: () => this.taxPrepData.activeRentals,
    create: (model) => {
      const index = this.taxPrepData.activeRentals.indexOf(model)
      const vm = new RentalAccordionViewModel(
        model,
        index,
        this.selectedYear.year,
        this._getOwners,
      )
      this.lifecycle.manage(vm)
      return vm
    },
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  readonly addDependentDialogVm = new AddDependentDialogViewModel({
    onSaved: (data) => this._addDependent(data),
  })

  readonly addBusinessDialogVm = new AddBusinessDialogViewModel({
    onSaved: (data) => this._addBusiness(data),
  })

  readonly addRentalDialogVm = new AddRentalDialogViewModel({
    onSaved: (data) => this._addRental(data),
  })

  readonly removeDependentDialogVm = new RemoveDependentDialogViewModel({
    onConfirm: (id) => this._archiveDependent(id),
  })

  readonly removeBusinessDialogVm = new RemoveBusinessDialogViewModel({
    onConfirm: (id) => this._archiveBusiness(id),
  })

  readonly removeRentalDialogVm = new RemoveRentalDialogViewModel({
    onConfirm: (id) => this._archiveRental(id),
  })

  @computed
  get dependentVms(): DependentAccordionViewModel[] {
    return this._dependentVmList.items
  }

  @computed
  get businessVms(): BusinessAccordionViewModel[] {
    return this._businessVmList.items
  }

  @computed
  get rentalVms(): RentalAccordionViewModel[] {
    return this._rentalVmList.items
  }

  readonly saveSectionDialogVm = new SaveSectionDialogViewModel({
    onSave: (target) => this.save({ type: 'step', step: target }),
    onNavigate: (target) => this.navigateToStep(target),
  })

  constructor(
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _txfWorklayerJobStore: TxfWorklayerJobStore,
    private readonly _getTxfWorklayerJob: () => TxfWorklayerJob,
    private readonly _getSelectedYear: () => FilingYear,

    private readonly _getTaxPrepData: () => TaxPreparationData,
    private readonly _fetchTaxPrepData: Task<[AbortSignal?], void>,
    private readonly _onYearChange: Task<[FilingYear], void>,
    private readonly _showConnectWithProDialog: () => void,
    private readonly _connectToConnectWithPro: () => boolean,
    private readonly _navigateToJobDetails: () => void,
  ) {}

  @computed
  get taxPrepData(): TaxPreparationData {
    return this._getTaxPrepData()
  }

  @computed
  get selectedYear(): FilingYear {
    return this._getSelectedYear()
  }

  @computed
  get nextYear(): FilingYear | undefined {
    const activeFilingYears = this._getTxfWorklayerJob().activeFilingYears
    const selectedYearIndex = activeFilingYears.findIndex(
      (filingYear) => filingYear.id === this.selectedYear.id,
    )

    return selectedYearIndex === -1
      ? undefined
      : activeFilingYears[selectedYearIndex + 1]
  }

  @computed
  get isLoading(): boolean {
    return this._fetchTaxPrepData.pending || this._onYearChange.pending
  }

  @computed
  get jobId() {
    return this._getTxfWorklayerJob().id
  }

  @computed
  get dependentCount(): number {
    return this.dependentVms.length
  }

  @computed
  get showSpouse(): boolean {
    const filingStatus = this.taxpayerVm.filingStatus ?? FilingStatus.Single
    const allowedFilingStatuses = [
      FilingStatus.MarriedFilingJointly,
      FilingStatus.MarriedFilingSeparately,
      FilingStatus.QualifiedSurvivingSpouse,
    ]
    return allowedFilingStatuses.includes(filingStatus)
  }

  private readonly _getOwners = (): Owner[] => {
    const owners: Owner[] = [{ type: 'UNASSIGNED', label: 'Unassigned' }]
    const basicInfo = this.taxPrepData.basicInformation

    if (basicInfo.taxPayer) {
      if (basicInfo.taxPayer.id) {
        owners.push({
          type: 'TAX_PAYER',
          label: formatFullName(
            basicInfo.taxPayer.firstName,
            basicInfo.taxPayer.middleInitial,
            basicInfo.taxPayer.lastName,
          ),
          taxPayerId: basicInfo.taxPayer.id,
        })
      }
    }

    if (basicInfo.spouse) {
      if (basicInfo.spouse.id) {
        owners.push({
          type: 'SPOUSE',
          label: formatFullName(
            basicInfo.spouse.firstName,
            basicInfo.spouse.middleInitial,
            basicInfo.spouse.lastName,
          ),
          spouseId: basicInfo.spouse.id,
        })
      }
    }

    if (basicInfo.taxPayer?.id && basicInfo.spouse?.id) {
      owners.push({
        type: 'JOINT',
        label: 'Joint',
        taxPayerId: basicInfo.taxPayer.id,
        spouseId: basicInfo.spouse.id,
      })
    }

    return owners
  }

  @action.bound
  selectStep(step: OrganizerStep) {
    if (this.selectedStep === step) return
    this.saveSectionDialogVm.showDialog(this.selectedStep, step)
  }

  @action.bound
  async navigateToStep(step: OrganizerStep) {
    this.showValidationError = false
    this.selectedStep = step
    await this._fetchTaxPrepData()
  }

  save = task.resolved(async (navigateTo?: NavigationTarget) => {
    if (!this._validateCurrentStep()) {
      this._expandInvalidAccordions()
      return
    }

    const props = this._buildSaveProps()
    await this._txfWorklayerJobStore
      .saveTxfTaxPreparationData(
        this.jobId,
        this.selectedYear.year,
        props,
        this.lifecycle.signal,
      )
      .catch(
        this._createSnackbarErrorHandler('Failed to save tax preparation data'),
      )

    this._newSnackbar((s) => s.withKey(SNACKBAR_KEY))
      .type('success')
      .autoDismiss()
      .saying(
        `${this.selectedStep} info for ${this.selectedYear.year} saved successfully!`,
      )
      .show()

    if (!navigateTo) {
      return
    }

    await match(navigateTo, 'type', {
      step: async ({ step }) => {
        if (!this._shouldNavigateToNextYear(step)) {
          return await this.navigateToStep(step)
        }

        if (this.nextYear) {
          return await this._handleYearTransition(this.nextYear)
        }

        this._handleFinalStepNavigation()
      },
      year: ({ year }) => this._onYearChange(year),
    })
  })

  private async _handleYearTransition(nextYear: FilingYear): Promise<void> {
    await this._onYearChange(nextYear)
    this.showValidationError = false
    this.selectedStep = OrganizerStep.Personal
  }

  private _handleFinalStepNavigation(): void {
    if (!this._connectToConnectWithPro()) {
      return this._navigateToJobDetails()
    }

    this._showConnectWithProDialog()
  }

  @computed
  private get _isOnLastStep(): boolean {
    return this.selectedStep === OrganizerStep.Rental
  }

  private _shouldNavigateToNextYear(targetStep: OrganizerStep): boolean {
    return this._isOnLastStep && targetStep === this.selectedStep
  }

  @action.bound
  private _expandInvalidAccordions() {
    match(this.selectedStep, {
      [OrganizerStep.Personal]: () => {
        this.taxpayerVm.isExpanded = !this.taxpayerVm.validation.isValid
        if (this.showSpouse) {
          this.spouseVm.isExpanded = !this.spouseVm.validation.isValid
        }
        this.dependentVms.forEach(
          (vm) => (vm.isExpanded = !vm.validation.isValid),
        )
      },
      [OrganizerStep.Business]: () => {
        this.businessVms.forEach(
          (vm) => (vm.isExpanded = !vm.validation.isValid),
        )
      },
      [OrganizerStep.Rental]: () => {
        this.rentalVms.forEach((vm) => (vm.isExpanded = !vm.validation.isValid))
      },
    })
  }

  @action.bound
  private _validateCurrentStep() {
    const isValid = match(this.selectedStep, {
      [OrganizerStep.Personal]: () => {
        this.taxpayerVm.validation.reset().validate()
        this.spouseVm.validation.reset().validate()
        this.dependentVms.forEach((vm) => vm.validation.reset().validate())

        return (
          this.taxpayerVm.validation.isValid &&
          (!this.showSpouse || this.spouseVm.validation.isValid) &&
          this.dependentVms.every((vm) => vm.validation.isValid)
        )
      },
      [OrganizerStep.Business]: () => {
        this.businessVms.forEach((vm) => vm.validation.reset().validate())
        return this.businessVms.every((vm) => vm.validation.isValid)
      },
      [OrganizerStep.Rental]: () => {
        this.rentalVms.forEach((vm) => vm.validation.reset().validate())
        return this.rentalVms.every((vm) => vm.validation.isValid)
      },
    })

    this.showValidationError = !isValid
    return isValid
  }

  private _buildSaveProps() {
    const modelProps = this.taxPrepData.toProps()
    const archivedDependents =
      this.taxPrepData.basicInformation.archivedDependents.map((d) =>
        d.toProps(),
      )
    const archivedBusinesses = this.taxPrepData.archivedBusinesses.map((b) =>
      b.toProps(),
    )
    const archivedRentals = this.taxPrepData.archivedRentals.map((r) =>
      r.toProps(),
    )

    return match(this.selectedStep, {
      [OrganizerStep.Personal]: () => ({
        ...modelProps,
        basicInformation: {
          taxPayer: this.taxpayerVm.toProps(),
          spouse: this.showSpouse ? this.spouseVm.toProps() : null,
          dependents: [
            ...this.dependentVms.map((vm) => vm.toProps()),
            ...archivedDependents,
          ],
        },
      }),
      [OrganizerStep.Business]: () => ({
        ...modelProps,
        businesses: [
          ...archivedBusinesses,
          ...this.businessVms.map((vm) => vm.toProps()),
        ],
      }),
      [OrganizerStep.Rental]: () => ({
        ...modelProps,
        rentals: [
          ...archivedRentals,
          ...this.rentalVms.map((vm) => vm.toProps()),
        ],
      }),
    })
  }

  @action.bound
  private _syncTaxpayerAndSpouseVms() {
    const basicInfo = this.taxPrepData.basicInformation
    this.taxpayerVm.reset(basicInfo.taxPayer)
    this._syncSpouseVm()
  }

  @action.bound
  private _syncSpouseVm() {
    const basicInfo = this.taxPrepData.basicInformation
    this.spouseVm.reset(
      basicInfo.spouse.id !== Id.none
        ? basicInfo.spouse.toProps()
        : {
            id: randomUUID(),
            firstName: null,
            middleInitial: null,
            lastName: null,
            tin: null,
            dob: null,
            phoneNumber: null,
            emailAddress: null,
            occupation: null,
          },
    )
  }

  @action.bound
  swapTaxpayerAndSpouse() {
    if (!this.taxpayerVm || !this.spouseVm) return

    const taxpayerData = {
      firstName: this.taxpayerVm.firstName,
      middleInitial: this.taxpayerVm.middleInitial,
      lastName: this.taxpayerVm.lastName,
      tin: this.taxpayerVm.tin,
      dateOfBirth: this.taxpayerVm.dateOfBirth,
      occupation: this.taxpayerVm.occupation,
      phoneNumber: this.taxpayerVm.phoneNumber,
      emailAddress: this.taxpayerVm.emailAddress,
    }

    const spouseData = {
      firstName: this.spouseVm.firstName,
      middleInitial: this.spouseVm.middleInitial,
      lastName: this.spouseVm.lastName,
      tin: this.spouseVm.tin,
      dateOfBirth: this.spouseVm.dateOfBirth,
      occupation: this.spouseVm.occupation,
      phoneNumber: this.spouseVm.phoneNumber,
      emailAddress: this.spouseVm.emailAddress,
    }

    this.taxpayerVm.firstName = spouseData.firstName
    this.taxpayerVm.middleInitial = spouseData.middleInitial
    this.taxpayerVm.lastName = spouseData.lastName
    this.taxpayerVm.tin = spouseData.tin
    this.taxpayerVm.dateOfBirth = spouseData.dateOfBirth
    this.taxpayerVm.occupation = spouseData.occupation
    this.taxpayerVm.phoneNumber = spouseData.phoneNumber
    this.taxpayerVm.emailAddress = spouseData.emailAddress

    this.spouseVm.firstName = taxpayerData.firstName
    this.spouseVm.middleInitial = taxpayerData.middleInitial
    this.spouseVm.lastName = taxpayerData.lastName
    this.spouseVm.tin = taxpayerData.tin
    this.spouseVm.dateOfBirth = taxpayerData.dateOfBirth
    this.spouseVm.occupation = taxpayerData.occupation
    this.spouseVm.phoneNumber = taxpayerData.phoneNumber
    this.spouseVm.emailAddress = taxpayerData.emailAddress
  }

  @action.bound
  private _addDependent(data: NewDependentData) {
    const newDependent = this.taxPrepData.basicInformation.dependents.set({
      id: randomUUID(),
      firstName: data.firstName,
      middleInitial: data.middleInitial,
      lastName: data.lastName,
      tin: data.tin,
      dob: data.dateOfBirth,
      relationship: data.relationship,
      archived: false,
    })

    // Wait for next tick to ensure the dependent is added to the list
    setTimeout(() => {
      const vm = this.dependentVms.find((v) => v.id === newDependent.id)
      if (vm) {
        vm.expand()
      }
    })
  }

  @action.bound
  private _archiveDependent(id: string) {
    this.taxPrepData.archiveDependent(id)
  }

  @action.bound
  showRemoveDependentDialog(vm: DependentAccordionViewModel) {
    this.removeDependentDialogVm.showDialog({
      id: Id.assert(vm.id),
      displayName: vm.fullName,
    })
  }

  @action.bound
  private _addBusiness(data: NewBusinessData) {
    const newBusiness = this.taxPrepData.businesses.set({
      id: randomUUID(),
      assignedTo: null,
      businessType: data.businessType,
      businessName: data.businessName,
      ein: data.ein,
      principleBusiness: data.principleBusiness,
      archived: false,
    })

    // Wait for next tick to ensure the business is added to the list
    setTimeout(() => {
      const vm = this.businessVms.find((v) => v.id === newBusiness.id)
      if (vm) {
        vm.expand()
      }
    })
  }

  @action.bound
  private _archiveBusiness(id: string) {
    this.taxPrepData.archiveBusiness(id)
  }

  @action.bound
  showRemoveBusinessDialog(vm: BusinessAccordionViewModel) {
    this.removeBusinessDialogVm.showDialog({
      id: Id.assert(vm.id),
      displayName: vm.displayName,
    })
  }

  @action.bound
  private _addRental(data: NewRentalData) {
    const newRental = this.taxPrepData.rentals.set({
      id: randomUUID(),
      assignedTo: null,
      propertyInformation: {
        type: data.propertyType,
        description: null,
      },
      address: data.address,
      archived: false,
    })

    // Wait for next tick to ensure the rental is added to the list
    setTimeout(() => {
      const vm = this.rentalVms.find((v) => v.id === newRental.id)
      if (vm) {
        vm.expand()
      }
    })
  }

  @action.bound
  private _archiveRental(id: string) {
    this.taxPrepData.archiveRental(id)
  }

  activate() {
    this.lifecycle.activate()

    this.taxpayerVm.activate()
    this.lifecycle.manage(this.taxpayerVm)
    this.spouseVm.activate()
    this.lifecycle.manage(this.spouseVm)

    this._dependentVmList.activate()
    this.lifecycle.manage(this._dependentVmList)
    this._businessVmList.activate()
    this.lifecycle.manage(this._businessVmList)
    this._rentalVmList.activate()
    this.lifecycle.manage(this._rentalVmList)

    this._disposeTaxpayerSpouseSync?.()
    this._disposeTaxpayerSpouseSync = reaction(
      () => this.taxPrepData,
      () => this._syncTaxpayerAndSpouseVms(),
      { fireImmediately: true },
    )

    this._disposeShowSpouseSync = reaction(
      () => this.showSpouse,
      () => this._syncSpouseVm(),
      { fireImmediately: true },
    )
  }

  deactivate() {
    this._disposeTaxpayerSpouseSync?.()
    this._disposeTaxpayerSpouseSync = null
    this._disposeShowSpouseSync?.()
    this._disposeShowSpouseSync = null
    this.lifecycle.deactivate()
  }
}
